import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

interface CartItem {
  id: string;
  cart_id?: string;
  product_id: string;
  quantity: number;
  price_type?: 'contado' | 'credito' | 'mayorista' | 'publico';
  product: {
    name: string;
    base_price: number;
    prices?: {
      price_type: 'contado' | 'credito' | 'mayorista' | 'publico';
      min_quantity: number;
      price: number;
      offer_price?: number;
      offer_days?: number;
    }[];
    main_image: string;
    min_order: number;
  };
}

interface CartContextType {
  cartItems: CartItem[];
  loading: boolean;
  error: string | null;
  refreshCart: () => Promise<void>;
  addToCartLocal: (productId: string, quantity: number, product: any) => void;
  removeFromCartLocal: (productId: string) => void;
  updateQuantityLocal: (productId: string, quantity: number) => void;
  clearLocalCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [localCart, setLocalCart] = useState<CartItem[]>([]);

  // Cargar carrito local del localStorage
  useEffect(() => {
    const savedLocalCart = localStorage.getItem('localCart');
    if (savedLocalCart) {
      try {
        setLocalCart(JSON.parse(savedLocalCart));
      } catch (error) {
        console.error('Error parsing local cart:', error);
        localStorage.removeItem('localCart');
      }
    }
  }, []);

  // Guardar carrito local en localStorage
  useEffect(() => {
    if (localCart.length > 0) {
      localStorage.setItem('localCart', JSON.stringify(localCart));
    } else {
      localStorage.removeItem('localCart');
    }
  }, [localCart]);

  const fetchCartItems = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // Usuario no autenticado, usar carrito local
        setCartItems(localCart);
        setLoading(false);
        return;
      }

      // Obtener o crear el carrito del usuario
      let { data: cart } = await supabase
        .from('cart')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!cart) {
        const { data: newCart, error: createError } = await supabase
          .from('cart')
          .insert([{ user_id: user.id }])
          .select('id')
          .single();

        if (createError) throw createError;
        cart = newCart;
      }

      // Obtener los items del carrito
      const { data, error } = await supabase
        .from('cart_items')
        .select(`
          id,
          cart_id,
          product_id,
          quantity,
          price_type,
          product:products(
            id,
            name,
            base_price,
            main_image,
            min_order,
            prices:product_prices(price_type, min_quantity, price, offer_price, offer_days)
          )
        `)
        .eq('cart_id', cart.id);

      if (error) throw error;
      
      // Agregar depuración para ver los datos
      console.log('Datos del carrito sin procesar:', data);
      
      // Verificar que cada item tenga la propiedad product
      const validItems = data?.filter(item => item && item.product && typeof item.product === 'object') || [];
      
      if (validItems.length !== data?.length) {
        console.warn('Algunos items del carrito no tienen datos de producto válidos', 
          data?.filter(item => !item || !item.product || typeof item.product !== 'object'));
      }
      
      console.log('Datos del carrito filtrados:', validItems);
      
      // Usar type assertion para evitar errores de tipo
      setCartItems(validItems as unknown as CartItem[]);
    } catch (err) {
      console.error('Error fetching cart:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar el carrito');
    } finally {
      setLoading(false);
    }
  }, [localCart]);

  const syncLocalCartWithServer = useCallback(async (sessionUser: any, cartItemsToSync: CartItem[]) => {
    if (!sessionUser || cartItemsToSync.length === 0) return;

    try {
      // Obtener o crear el carrito del usuario
      let { data: cart } = await supabase
        .from('cart')
        .select('id')
        .eq('user_id', sessionUser.id)
        .single();

      if (!cart) {
        const { data: newCart, error: createError } = await supabase
          .from('cart')
          .insert([{ user_id: sessionUser.id }])
          .select('id')
          .single();

        if (createError) throw createError;
        cart = newCart;
      }

      // Obtener el tipo de precio del usuario
      const { data: profile } = await supabase
        .from('profiles')
        .select('price_type')
        .eq('id', sessionUser.id)
        .single();

      const userPriceType = profile?.price_type || 'publico';

      // Agregar cada item del carrito local al servidor
      for (const localItem of cartItemsToSync) {
        const { error: insertError } = await supabase
          .from('cart_items')
          .insert([{
            cart_id: cart.id,
            product_id: localItem.product_id,
            quantity: localItem.quantity,
            price_type: userPriceType
          }]);

        if (insertError && insertError.code !== '23505') { // Ignorar errores de duplicado
          console.error('Error syncing cart item:', insertError);
        }
      }

      // Limpiar carrito local
      setLocalCart([]);
      localStorage.removeItem('localCart');

      // Recargar carrito del servidor
      await fetchCartItems();
    } catch (error) {
      console.error('Error syncing local cart:', error);
    }
  }, [fetchCartItems]);

  // Detectar cambios en autenticación
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const authenticatedUser = session?.user ?? null;
      if (authenticatedUser) {
        // Usuario se autenticó, sincronizar carrito local con servidor
        const cartSnapshot = [...localCart];
        syncLocalCartWithServer(authenticatedUser, cartSnapshot);
      } else {
        // Usuario se desautenticó, usar carrito local
        setCartItems(localCart);
      }
    });

    return () => subscription.unsubscribe();
  }, [localCart, syncLocalCartWithServer]);

  // Funciones para manejar carrito local
  const addToCartLocal = (productId: string, quantity: number, product: any) => {
    setLocalCart(prev => {
      const existingItem = prev.find(item => item.product_id === productId);
      if (existingItem) {
        return prev.map(item => 
          item.product_id === productId 
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        return [...prev, {
          id: `local-${Date.now()}`,
          product_id: productId,
          quantity,
          product
        }];
      }
    });
  };

  const removeFromCartLocal = (productId: string) => {
    setLocalCart(prev => prev.filter(item => item.product_id !== productId));
  };

  const updateQuantityLocal = (productId: string, quantity: number) => {
    setLocalCart(prev => 
      prev.map(item => 
        item.product_id === productId 
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearLocalCart = () => {
    setLocalCart([]);
    localStorage.removeItem('localCart');
  };

  // Efecto para actualizar cartItems cuando cambia localCart para usuarios no autenticados
  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setCartItems(localCart);
      }
    };
    checkUser();
  }, [localCart]);

  useEffect(() => {
    fetchCartItems();
    // Removido: suscripción en tiempo real innecesaria que consume egress
    // El carrito se actualiza manual o mediante refreshCart()
  }, [fetchCartItems]);

  return (
    <CartContext.Provider value={{ 
      cartItems, 
      loading, 
      error, 
      refreshCart: fetchCartItems,
      addToCartLocal,
      removeFromCartLocal,
      updateQuantityLocal,
      clearLocalCart
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCartContext() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCartContext must be used within a CartProvider');
  }
  return context;
} 
